import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Box, Grid, Paper, Skeleton, Tooltip, Typography } from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import LucideIcon from "../../components/LucideIcon";

import { hostname } from "../../utils";
import { DEMO_MODE_KEY } from "../../constants";
import {
  DEMO_MARKETING_CAMPAIGN_METRICS,
  generateDemoMarketingCampaignTimeseries,
} from "../../constants/demoData";

const CompactMetric = ({
  icon,
  title,
  value,
  tooltip,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
  tooltip: string;
}) => (
  <Tooltip title={tooltip} arrow placement="top">
    <Paper elevation={1} sx={{ px: 1.5, py: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
        <Box sx={{ color: "text.secondary", display: "flex", "& svg": { width: 16, height: 16 } }}>
          {icon}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="h6" sx={{ lineHeight: 1.1, fontWeight: 600 }}>
        {value}
      </Typography>
    </Paper>
  </Tooltip>
);

interface CampaignMetrics {
  totalCampaigns: number;
  activeCampaigns: number;
  totalSent: number;
  totalOpened: number;
  totalClicked: number;
  openRate: number;
  clickRate: number;
}

interface TimeseriesSeries {
  id: string;
  name: string;
  label: string;
  data: number[];
}

interface TimeseriesResponse {
  dates: string[];
  series: TimeseriesSeries[];
}

interface Props {
  companyId: string;
  getToken: () => Promise<string>;
  showMetrics?: boolean;
}

const formatNumber = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

const formatPercent = (r: number): string => `${(r * 100).toFixed(1)}%`;

// Matches the sequence ActivityTimeline palette so campaign metrics feel like
// a peer of the sequence activity chart: Sent blue, Opens green, Clicks orange.
const SERIES_COLORS: Record<string, string> = {
  sent: "#1976d2",
  opens: "#4caf50",
  clicks: "#ff9800",
};

const formatHourTick = (iso: string) => {
  const d = new Date(iso);
  if (d.getHours() === 0) {
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
  return "";
};

const formatHourTooltip = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
  });
};

const MarketingCampaignsMetricsBar: React.FC<Props> = ({
  companyId,
  getToken,
  showMetrics = true,
}) => {
  const [metrics, setMetrics] = useState<CampaignMetrics | null>(null);
  const [timeseries, setTimeseries] = useState<TimeseriesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(
    () => localStorage.getItem(DEMO_MODE_KEY) === "true"
  );

  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === DEMO_MODE_KEY) {
        setIsDemoMode(e.newValue === "true");
      }
    };
    const checkDemoMode = () => {
      const currentValue = localStorage.getItem(DEMO_MODE_KEY) === "true";
      setIsDemoMode((prev) => (prev !== currentValue ? currentValue : prev));
    };
    window.addEventListener("storage", handleStorageChange);
    const interval = setInterval(checkDemoMode, 500);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (isDemoMode) {
      setLoading(true);
      const timer = setTimeout(() => {
        if (!cancelled) {
          setMetrics(DEMO_MARKETING_CAMPAIGN_METRICS);
          setTimeseries(generateDemoMarketingCampaignTimeseries());
          setLoading(false);
        }
      }, 300);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }

    const load = async () => {
      setLoading(true);
      try {
        const token = await getTokenRef.current();
        const [statsRes, seriesRes] = await Promise.all([
          axios.get(`${hostname}/marketing-campaigns/statistics`, {
            params: { companyId },
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${hostname}/marketing-campaigns/timeseries`, {
            params: { companyId, days: 7 },
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        if (!cancelled) {
          setMetrics(statsRes.data ?? null);
          setTimeseries(seriesRes.data ?? null);
        }
      } catch (e) {
        if (!cancelled) {
          setMetrics(null);
          setTimeseries(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [companyId, isDemoMode]);

  if (loading) {
    return (
      <Box sx={{ mt: 2, mb: 2 }}>
        {showMetrics && (
          <Grid container spacing={1} sx={{ mb: 1 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={6} sm={4} md={2} key={i}>
                <Skeleton variant="rounded" height={56} />
              </Grid>
            ))}
          </Grid>
        )}
        <Skeleton variant="rounded" height={160} />
      </Box>
    );
  }

  if (!metrics || metrics.totalCampaigns === 0) {
    return null;
  }

  const chartSeries =
    timeseries?.series.map((s) => ({
      label: s.label,
      data: s.data,
      color: SERIES_COLORS[s.id] ?? "#1976d2",
      stack: "events",
      area: true,
      showMark: false,
      curve: "monotoneX" as const,
    })) ?? [];

  const chartDates = timeseries?.dates ?? [];
  const hasChartData = chartSeries.some((s) => s.data.some((v) => v > 0));

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      {showMetrics && (
        <Grid container spacing={1} sx={{ mb: 1 }}>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<LucideIcon name="Megaphone" />}
              title="Campaigns"
              value={formatNumber(metrics.totalCampaigns)}
              tooltip="Total marketing campaigns"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<LucideIcon name="CirclePlay" />}
              title="Active"
              value={formatNumber(metrics.activeCampaigns)}
              tooltip="Scheduled or currently sending"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<LucideIcon name="Send" />}
              title="Sent"
              value={formatNumber(metrics.totalSent)}
              tooltip="Total emails sent across all campaigns"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<LucideIcon name="Eye" />}
              title="Opens"
              value={formatNumber(metrics.totalOpened)}
              tooltip="Unique recipients who opened an email"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<LucideIcon name="Pointer" />}
              title="Clicks"
              value={formatNumber(metrics.totalClicked)}
              tooltip="Unique recipients who clicked a link"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<LucideIcon name="Percent" />}
              title="Open Rate"
              value={formatPercent(metrics.openRate)}
              tooltip="Opens divided by sent"
            />
          </Grid>
        </Grid>
      )}
      <Paper elevation={1} sx={{ px: 1.5, py: 1 }}>
        {hasChartData ? (
          <Box sx={{ width: "100%", height: 160 }}>
            <LineChart
              xAxis={[
                {
                  scaleType: "point",
                  data: chartDates,
                  valueFormatter: (value: string, context: any) =>
                    context?.location === "tooltip"
                      ? formatHourTooltip(value)
                      : formatHourTick(value),
                  tickLabelInterval: (value: string) => {
                    const d = new Date(value);
                    return d.getHours() === 0;
                  },
                },
              ]}
              yAxis={[{ disableLine: true, disableTicks: true }]}
              series={chartSeries}
              height={160}
              margin={{ top: 10, right: 10, bottom: 24, left: 10 }}
              slotProps={{ legend: { hidden: true } }}
              sx={{
                "& .MuiAreaElement-root": { fillOpacity: 0.35 },
                "& .MuiChartsAxis-tickLabel": { fontSize: "0.7rem" },
                "& .MuiChartsAxis-left .MuiChartsAxis-tickLabel": { display: "none" },
              }}
            />
          </Box>
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", py: 1, textAlign: "center" }}
          >
            No campaign activity in the last 7 days yet.
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default MarketingCampaignsMetricsBar;
